import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetCourseBySlugQuery,
  useGetCourseResultQuery,
} from "@/features/api/courseApi";

import {
  User,
  BookOpen,
  Award,
  CheckCircle2,
  History,
  Loader2,
} from "lucide-react";

export default function CourseResult() {
  const { courseSlug } = useParams();
  const navigate = useNavigate();

  /* ---------------- FETCH COURSE ---------------- */
  const {
    data: courseRes,
    isLoading: courseLoading,
    error: courseError,
  } = useGetCourseBySlugQuery(courseSlug, {
    skip: !courseSlug,
  });

  /* ---------------- NORMALIZE COURSE ---------------- */
  const course = useMemo(() => {
    const src = courseRes?.course;
    if (!src) return null;

    return {
      _id: src._id,
      slug: src.slug,
      title: src.title || "Untitled Course",
      description: src.content || "No description provided.",
      lessons: Array.isArray(src.lessons)
        ? src.lessons.map((l) => ({
            _id: l._id,
            title: l.title,
          }))
        : [],
    };
  }, [courseRes]);

  const courseId = course?._id;

  /* ---------------- FETCH RESULT ---------------- */
  const {
    data: rawResult,
    isLoading: resultLoading,
    error: resultError,
  } = useGetCourseResultQuery(courseId, {
    skip: !courseId,
    refetchOnMountOrArgChange: true,
  });

  /* ---------------- NORMALIZE RESULT ---------------- */
  const { results, user } = useMemo(() => {
    if (!rawResult) return { results: [], user: {} };

    return {
      results: Array.isArray(rawResult.results)
        ? rawResult.results
        : [],
      user: rawResult.user || {},
    };
  }, [rawResult]);

  /* ---------------- LOADING ---------------- */
  if (courseLoading || resultLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 text-gray-600">
        <Loader2 className="animate-spin h-6 w-6 mr-2" />
        <span>Loading course results...</span>
      </div>
    );
  }

  /* ---------------- ERROR ---------------- */
  const errorMessage =
    courseError?.data?.message ||
    resultError?.data?.message ||
    "Failed to load course results.";

  if (courseError || resultError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-red-600 p-6">
        <p className="text-lg font-semibold mb-4">⚠️ {errorMessage}</p>
        <div className="flex gap-4">
          <button
            onClick={() => navigate(`/courses/${courseSlug}`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md"
          >
            Go to Course
          </button>
          <button
            onClick={() => navigate("/courses")}
            className="px-4 py-2 border rounded-md"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  /* ---------------- NO COURSE ---------------- */
  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        Course not found.
      </div>
    );
  }

  /* ---------------- NO RESULTS ---------------- */
  if (results.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-center">
        <p className="text-lg font-semibold mb-4">
          You haven’t completed this course yet.
        </p>
        <button
          onClick={() => navigate(`/courses/${courseSlug}`)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md"
        >
          Go to Course
        </button>
      </div>
    );
  }

  /* ---------------- PROCESS RESULT ---------------- */
  const latest = results[results.length - 1];
  const totalLessons = course.lessons.length;

  const completedLessons =
    Array.isArray(latest.completedLessons)
      ? latest.completedLessons.length
      : 0;

  const percentage =
    totalLessons > 0
      ? Math.round((completedLessons / totalLessons) * 100)
      : 0;

  const completedDate = latest.completedAt
    ? new Date(latest.completedAt).toLocaleDateString()
    : "—";

  const score = Number(latest.score || 0);
  const attempts = results.length;

  /* ---------------- RETAKE ---------------- */
  const handleRetake = () => {
    navigate(`/courses/${courseSlug}`);
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-8">

        {/* HEADER */}
        <div className="text-center">
          <h1 className="text-3xl font-bold">
            {course.title} – Results
          </h1>
          <p className="text-gray-600 mt-2">
            Your course performance summary
          </p>
        </div>

        {/* USER INFO */}
        <Section title="Student Information" icon={User}>
          <Info label="Name" value={user.name || "N/A"} />
          <Info label="Email" value={user.email || "N/A"} />
        </Section>

        {/* COURSE INFO */}
        <Section title="Course Information" icon={BookOpen}>
          <Info label="Course" value={course.title} />
          <Info label="Last Completed" value={completedDate} />
          <Info label="Total Lessons" value={totalLessons} />
        </Section>

        {/* RESULT */}
        <Section title="Latest Attempt" icon={Award}>
          <Info
            label="Completed Lessons"
            value={`${completedLessons} / ${totalLessons}`}
          />
          <Info label="Score" value={`${score} points`} />
          <Info label="Percentage" value={`${percentage}%`} />

          <button
            onClick={handleRetake}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md"
          >
            <CheckCircle2 className="h-4 w-4" />
            Retake Course
          </button>
        </Section>

        {/* HISTORY */}
        {results.length > 1 && (
          <Section title="Attempt History" icon={History}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="p-2">Attempt</th>
                  <th className="p-2">Date</th>
                  <th className="p-2">Score</th>
                  <th className="p-2">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => {
                  const completed =
                    r.completedLessons?.length || 0;
                  const pct =
                    totalLessons > 0
                      ? Math.round(
                          (completed / totalLessons) * 100
                        )
                      : 0;

                  return (
                    <tr key={i} className="border-b">
                      <td className="p-2">{i + 1}</td>
                      <td className="p-2">
                        {r.completedAt
                          ? new Date(
                              r.completedAt
                            ).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="p-2">{r.score ?? 0}</td>
                      <td className="p-2">{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Section>
        )}
      </div>
    </div>
  );
}

/* ---------------- SMALL HELPERS ---------------- */

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white shadow rounded-lg p-6 space-y-4">
      <h2 className="flex items-center gap-2 text-xl font-semibold">
        <Icon className="h-5 w-5 text-blue-600" />
        {title}
      </h2>
      <div className="grid sm:grid-cols-2 gap-4 text-sm">
        {children}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <p>
      <span className="font-medium">{label}:</span>{" "}
      {value}
    </p>
  );
}
